// Search-optimized guide pages. Each answers a real high-intent query, then upsells the full pack.
export const GUIDES = [
  {
    slug: "stop-your-phone-tracking-you",
    node: "01",
    metaTitle: "How to Stop Your Phone From Tracking You (2026)",
    metaDescription: "A plain-language guide to stop your iPhone or Android from tracking your location, ads and activity. Real steps you can do in minutes.",
    h1: "How to stop your phone from tracking you",
    intro: "Your phone quietly logs where you go, what you tap and who you are, all day. Here's how to shut most of it down in a few minutes, no tech skills needed.",
    steps: [
      { h: "Turn off location history", body: "On iPhone: Settings → Privacy & Security → Location Services → System Services → turn off Significant Locations. On Android: Settings → Location → Location Services → turn off Google Location History and Timeline." },
      { h: "Reset your advertising ID", body: "iPhone: Settings → Privacy → Tracking → turn off 'Allow Apps to Request to Track'. Android: Settings → Privacy → Ads → Delete advertising ID." },
      { h: "Cut background app refresh", body: "Disable background refresh for apps that don't need it. Fewer apps running in the background means fewer silent pings home with your location." },
    ],
    related: ["stop-apps-tracking-your-location", "delete-google-search-history"],
  },
  {
    slug: "delete-google-search-history",
    node: "02",
    metaTitle: "How to Delete Your Google Search & Activity History",
    metaDescription: "Delete your entire Google search and activity history and set it to auto-delete so Google stops hoarding what you do.",
    h1: "How to delete your Google search & activity history",
    intro: "Google keeps a running record of nearly everything you search, watch and say. You can wipe it and stop it from rebuilding.",
    steps: [
      { h: "Open your activity", body: "Go to myactivity.google.com and sign in. This is the full record Google keeps on you." },
      { h: "Delete everything", body: "Click 'Delete' → 'All time' to wipe your history in one pass." },
      { h: "Turn on auto-delete", body: "Under Web & App Activity, set auto-delete to 3 months so it never piles up again. Switch YouTube History and Location History off entirely." },
    ],
    related: ["remove-your-name-from-google-search", "delete-voice-recordings"],
  },
  {
    slug: "remove-your-name-from-google-search",
    node: "03",
    metaTitle: "How to Remove Your Name From Google Search Results",
    metaDescription: "Step-by-step: remove your personal info from Google search results and push down old pages that expose your name, address or phone.",
    h1: "How to remove your name from Google search results",
    intro: "If your home address, phone number or old accounts show up when someone Googles you, you can ask Google to take them down.",
    steps: [
      { h: "Use Google's removal tool", body: "Go to Google's 'Results about you' tool and submit any result showing your contact info. Google now removes personal-info results on request." },
      { h: "Kill it at the source", body: "A search result only exists because a site publishes it. Opt out of the data broker or people-search site behind the result (see our broker guide) so it can't come back." },
      { h: "Refresh the cache", body: "After the source page is gone, use the removal tool again to clear the outdated cached copy." },
    ],
    related: ["remove-yourself-from-whitepages", "delete-google-search-history"],
  },
  {
    slug: "remove-yourself-from-whitepages",
    node: "04",
    metaTitle: "How to Remove Yourself From Whitepages, Spokeo & BeenVerified",
    metaDescription: "Free step-by-step opt-out for Whitepages, Spokeo and BeenVerified so your address, phone and relatives stop showing up online.",
    h1: "How to remove yourself from Whitepages, Spokeo & BeenVerified",
    intro: "Data brokers publish your home address, phone number and family for anyone to find. Here's how to opt out of the big three, the ones that feed everyone else.",
    steps: [
      { h: "Whitepages", body: "Find your listing on whitepages.com, copy the URL, then go to whitepages.com/suppression-requests, paste it and confirm by phone. Removal takes about 24 hours." },
      { h: "Spokeo", body: "Search your name at spokeo.com, copy the profile URL, then go to spokeo.com/optout, paste it and enter your email to confirm." },
      { h: "BeenVerified", body: "Go to beenverified.com/app/optout/search, find your record and remove it. Then recheck monthly, brokers relist you." },
    ],
    related: ["remove-your-name-from-google-search", "stop-apps-tracking-your-location"],
  },
  {
    slug: "delete-voice-recordings",
    node: "05",
    metaTitle: "How to Delete Alexa, Siri & Google Voice Recordings",
    metaDescription: "Delete the voice recordings Amazon, Apple and Google keep, and stop your smart speakers from saving what you say.",
    h1: "How to delete Alexa, Siri & Google voice recordings",
    intro: "Your smart speakers and phone assistants save clips of your voice. You can delete them and turn off future recording.",
    steps: [
      { h: "Amazon Alexa", body: "In the Alexa app: Settings → Alexa Privacy → Review Voice History → delete all. Then set 'Don't save recordings'." },
      { h: "Apple Siri", body: "Settings → Siri & Search → Siri & Dictation History → Delete. Opt out of 'Improve Siri & Dictation'." },
      { h: "Google Assistant", body: "At myactivity.google.com, open Web & App Activity, filter by Voice & Audio and delete. Turn off audio saving." },
    ],
    related: ["turn-off-face-id-tracking", "delete-google-search-history"],
  },
  {
    slug: "stop-browser-fingerprinting",
    node: "06",
    metaTitle: "How to Stop Browser Fingerprinting & Tracking",
    metaDescription: "Browsers leak a unique 'fingerprint' that tracks you even without cookies. Here's how to blunt it with free settings and tools.",
    h1: "How to stop browser fingerprinting & tracking",
    intro: "Sites identify you by your browser's unique fingerprint, even in incognito. You can make yourself far harder to single out.",
    steps: [
      { h: "Switch to a hardened browser", body: "Use Brave or Firefox with strict tracking protection. Both actively fight fingerprinting out of the box." },
      { h: "Block third-party cookies", body: "In your browser's privacy settings, block third-party cookies and enable 'Do Not Track' and HTTPS-only mode." },
      { h: "Stop cross-site trackers", body: "Add uBlock Origin. It blocks the invisible scripts that follow you from site to site." },
    ],
    related: ["stop-apps-tracking-your-location", "make-social-media-private"],
  },
  {
    slug: "stop-apps-tracking-your-location",
    node: "07",
    metaTitle: "How to Stop Apps From Tracking Your Location",
    metaDescription: "Stop apps from harvesting and selling your location. Exact iPhone and Android steps to lock down location, Wi-Fi and Bluetooth tracking.",
    h1: "How to stop apps from tracking your location",
    intro: "Many apps collect your location constantly and sell it. Here's how to cut them off without losing the features you actually use.",
    steps: [
      { h: "Set location to 'While Using'", body: "iPhone: Settings → Privacy → Location Services. Android: Settings → Location → App permissions. Change every app from 'Always' to 'While Using' or 'Never'." },
      { h: "Turn off precise location", body: "For apps that don't need your exact spot (weather, shopping), turn off 'Precise Location' so they only get a rough area." },
      { h: "Disable Wi-Fi/Bluetooth scanning", body: "On Android, turn off Wi-Fi and Bluetooth scanning under Location services, these track you even when both are off." },
    ],
    related: ["stop-your-phone-tracking-you", "remove-yourself-from-whitepages"],
  },
  {
    slug: "make-social-media-private",
    node: "08",
    metaTitle: "How to Make Your Social Media Private & Untraceable",
    metaDescription: "Lock down Facebook, Instagram and more: private profiles, no tagging, no facial recognition and no third-party data sharing.",
    h1: "How to make your social media private & untraceable",
    intro: "Your social profiles are a goldmine for anyone trying to find or profile you. Here's how to close the doors.",
    steps: [
      { h: "Go fully private", body: "Set every profile to private and hide your friends/followers list, birthday, hometown and email from public view." },
      { h: "Kill tagging & facial recognition", body: "Turn off face recognition and require your approval before you're tagged in anything." },
      { h: "Revoke connected apps", body: "In each platform's settings, remove third-party apps you've logged in with, they quietly siphon your data." },
    ],
    related: ["stop-browser-fingerprinting", "protect-your-accounts-from-hackers"],
  },
  {
    slug: "turn-off-face-id-tracking",
    node: "09",
    metaTitle: "How to Turn Off Face ID & Biometric Tracking",
    metaDescription: "Reduce biometric exposure: manage Face ID, voice profiles and smart-home sensors so your face and voice aren't a database entry.",
    h1: "How to turn off face & voice tracking",
    intro: "Your face and voice are becoming permanent IDs. Here's how to limit where they're stored and used.",
    steps: [
      { h: "Audit biometric logins", body: "Keep Face ID / fingerprint for your device lock (it stays on-device), but avoid handing biometrics to random third-party apps." },
      { h: "Delete voice profiles", body: "Remove saved 'Voice Match' profiles from Google and Alexa so they stop recognizing and logging your voice." },
      { h: "Mute smart-home sensors", body: "Disable cameras and mics on smart displays when not in use, and turn off cloud upload of clips." },
    ],
    related: ["delete-voice-recordings", "stop-apps-accessing-mic-camera"],
  },
  {
    slug: "stop-apps-accessing-mic-camera",
    node: "10",
    metaTitle: "How to Stop Apps Accessing Your Mic, Camera & Location",
    metaDescription: "Audit and revoke app permissions on iPhone and Android so apps can't reach your microphone, camera, contacts or location behind your back.",
    h1: "How to stop apps accessing your mic, camera & location",
    intro: "Most people never check which apps can hear, see and locate them. A five-minute audit fixes years of over-sharing.",
    steps: [
      { h: "Open the permission manager", body: "iPhone: Settings → Privacy & Security. Android: Settings → Privacy → Permission manager. Review mic, camera, contacts and location one by one." },
      { h: "Revoke what doesn't fit", body: "A flashlight app doesn't need your contacts. Turn off any permission that doesn't match what the app actually does." },
      { h: "Watch the indicators", body: "The orange/green dot means mic or camera is active. If it lights up unexpectedly, find and cut the culprit." },
    ],
    related: ["stop-apps-tracking-your-location", "turn-off-face-id-tracking"],
  },
  {
    slug: "protect-your-accounts-from-hackers",
    node: "11",
    metaTitle: "How to Protect Your Accounts From Being Hacked",
    metaDescription: "The password and 2FA setup that stops the #1 way people get hacked. Simple, no jargon, done in 15 minutes.",
    h1: "How to protect your accounts from being hacked",
    intro: "Most breaches come from reused passwords and weak email security. Fix these and you close the door on the vast majority of attacks.",
    steps: [
      { h: "Fix your email first", body: "Your email resets every other password, so give it a long, unique passphrase and turn on app-based 2FA today." },
      { h: "Use a password manager", body: "Let a password manager generate and store a unique password for every account. Stop reusing the same one everywhere." },
      { h: "Ditch SMS 2FA where you can", body: "Use an authenticator app instead of text-message codes, SMS can be hijacked with a SIM swap." },
    ],
    related: ["make-social-media-private", "privacy-maintenance-checklist"],
  },
  {
    slug: "privacy-maintenance-checklist",
    node: "12",
    metaTitle: "How to Stay Private Long-Term (Maintenance Checklist)",
    metaDescription: "Privacy isn't one-and-done. A simple recurring checklist to stay off data brokers and keep your info offline for good.",
    h1: "How to stay private long-term",
    intro: "Data brokers relist you and settings reset with updates. A light recurring routine keeps you invisible without the grind.",
    steps: [
      { h: "Re-check brokers quarterly", body: "Every 3 to 6 months, re-search the major people-search sites and re-opt-out, they quietly rebuild your profile." },
      { h: "Re-Google yourself monthly", body: "Search your name in an incognito window monthly and clear anything new with Google's removal tool." },
      { h: "Recheck settings after updates", body: "Big OS updates sometimes flip privacy toggles back on. Do a quick pass after each major update." },
    ],
    related: ["remove-yourself-from-whitepages", "protect-your-accounts-from-hackers"],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
